import React, { useState } from 'react';
import { Play, Heart, Clock } from 'lucide-react';
import { Button } from './ui/button';
import { useLang } from '../context/LangContext';
import { templates } from '../mock/data';
import TemplateModal from './TemplateModal';

const Templates = () => {
  const { t } = useLang();
  const [filter, setFilter] = useState('All');
  const [selected, setSelected] = useState(null);
  const cats = ['All', 'TikTok', 'Reels', 'YouTube'];
  const list = filter === 'All' ? templates : templates.filter(x => x.category === filter);

  return (
    <section id="templates" className="py-24 md:py-32 bg-white">
      <div className="max-w-[1400px] mx-auto px-5 md:px-10">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-10">
          <div className="max-w-2xl">
            <span className="inline-block text-[13px] font-bold tracking-widest uppercase text-fuchsia-600 mb-4">{t.templates.sectionLabel}</span>
            <h2 className="text-[40px] md:text-[56px] font-black tracking-[-0.02em] leading-[1.05] text-neutral-900">{t.templates.title}</h2>
            <p className="mt-4 text-[16px] text-neutral-600">{t.templates.subtitle}</p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {cats.map(c => (
              <button key={c} onClick={() => setFilter(c)} className={`px-4 h-10 rounded-full text-[13.5px] font-semibold transition-all ${filter===c ? 'bg-neutral-900 text-white' : 'bg-neutral-100 text-neutral-700 hover:bg-neutral-200'}`}>{c}</button>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5">
          {list.map(tpl => (
            <div key={tpl.id} onClick={() => setSelected(tpl)} className="group relative rounded-2xl overflow-hidden bg-neutral-900 aspect-[9/16] cursor-pointer">
              <img src={tpl.img} alt={tpl.title} className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/10 to-black/40" />
              <div className="absolute top-3 left-3 rtl:left-auto rtl:right-3 flex items-center gap-1 bg-black/50 backdrop-blur-md rounded-full px-2.5 py-1">
                <Clock className="w-3 h-3 text-white" />
                <span className="text-[11px] font-semibold text-white">{tpl.duration}</span>
              </div>
              <div className="absolute top-3 right-3 rtl:right-auto rtl:left-3 bg-white/95 rounded-full px-2 py-1">
                <span className="text-[10px] font-bold text-neutral-900 uppercase">{tpl.category}</span>
              </div>
              <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                <div className="w-14 h-14 rounded-full bg-white/95 flex items-center justify-center shadow-2xl scale-90 group-hover:scale-100 transition-transform">
                  <Play className="w-6 h-6 fill-black text-black ml-0.5" />
                </div>
              </div>
              <div className="absolute bottom-0 left-0 right-0 p-4">
                <h4 className="text-white font-bold text-[15px] mb-1 tracking-tight">{tpl.title}</h4>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-1 text-white/80">
                    <Heart className="w-3 h-3 fill-white/80" />
                    <span className="text-[11px] font-medium">{tpl.uses}</span>
                  </div>
                  <button className="text-[11px] font-bold text-white bg-black/40 backdrop-blur-sm rounded-full px-2.5 py-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    {t.templates.useTemplate}
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-12 text-center">
          <Button variant="outline" className="h-12 px-8 rounded-full border-2 border-neutral-200 hover:border-neutral-900 hover:bg-transparent text-neutral-900 font-semibold">
            {t.templates.viewAll}
          </Button>
        </div>
      </div>
      {selected && <TemplateModal template={selected} onClose={() => setSelected(null)} />}
    </section>
  );
};

export default Templates;
